"use client";

import React from "react";
import {
  Paper,
  Stack,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  Box,
  TablePagination,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DeleteIcon from "@mui/icons-material/Delete";

// --- Type Definitions (Kept from original) ---
interface PropertiesModel {
  page: number;
  pages: number;
  rowsPerPage: number;
  totalRows: number; // Added to support MUI Pagination
  disableEdit?: boolean;
  disableSee?: boolean;
  disableDelete?: boolean;
}

interface RowActionModel {
  deleteAction: (row: any) => void;
  seeAction: (row: any) => void;
  editAction: (row: any) => void;
}

interface ComponentProps<T> {
  columns: string[];
  rows: T[];
  pageChange: (page: number) => void;
  properties: PropertiesModel;
  rowActions: RowActionModel;
}

// --- Main Advanced Table Component ---
export default function AdvancedTable<T extends { id: any }>({
  columns,
  rows,
  pageChange,
  properties: {
    rowsPerPage,
    page,
    pages,
    totalRows,
    disableDelete,
    disableSee,
    disableEdit,
  },
  rowActions: { deleteAction, editAction, seeAction },
}: ComponentProps<T>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handlePageChange = (event: unknown, newPage: number) => {
    // MUI pagination is 0-indexed, original component seems 1-indexed. Adjusting.
    pageChange(newPage + 1);
  };

  // --- Responsive Mobile Card Layout ---
  if (isMobile) {
    return (
      <Stack spacing={2}>
        {rows.length === 0 ? (
          <Alert severity="info">Nincsenek megjeleníthető adatok.</Alert>
        ) : (
          rows.map((row) => (
            <Paper key={row.id} sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1}>
                {Object.values(row).map((value, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {columns[index]}:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      textAlign="right"
                    >
                      {String(value)}
                    </Typography>
                  </Box>
                ))}
                {(!disableDelete || !disableEdit || !disableSee) && (
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={1}
                    sx={{ pt: 1 }}
                  >
                    {!disableEdit && (
                      <IconButton size="small" onClick={() => editAction(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {!disableSee && (
                      <IconButton size="small" onClick={() => seeAction(row)}>
                        <RemoveRedEyeIcon fontSize="small" />
                      </IconButton>
                    )}
                    {!disableDelete && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteAction(row)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>
          ))
        )}
        <TablePagination
          component="div"
          count={totalRows}
          page={page - 1} // Adjust for 0-based index
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]} // Hide rows per page change if not needed
          labelRowsPerPage="Sor/oldal:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count}`
          }
        />
      </Stack>
    );
  }

  // --- Desktop Table Layout ---
  return (
    <Paper sx={{ width: "100%", overflow: "hidden", borderRadius: 2 }}>
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          sx={{ width: "100%", borderCollapse: "collapse" }}
        >
          <Box component="thead">
            <Box
              component="tr"
              sx={{
                "& th": {
                  p: 2,
                  textAlign: "left",
                  fontWeight: "bold",
                  color: "text.secondary",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {!(disableDelete && disableEdit && disableSee) && (
                <Box component="th" sx={{ width: 120 }}>
                  Műveletek
                </Box>
              )}
              {columns.map((column, i) => (
                <Box component="th" key={column + " " + i}>
                  {column}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.slice(0, rowsPerPage).map((row, i) => (
              <Box
                component="tr"
                key={row.id || i}
                sx={{
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  "& td": {
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                {(!disableDelete || !disableEdit || !disableSee) && (
                  <Box component="td">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-start"
                    >
                      {!disableEdit && (
                        <Tooltip title={t("Edit") as string}>
                          <IconButton
                            onClick={() => editAction(row)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!disableSee && (
                        <Tooltip title={t("See") as string}>
                          <IconButton
                            onClick={() => seeAction(row)}
                            size="small"
                          >
                            <RemoveRedEyeIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!disableDelete && (
                        <Tooltip title={t("Delete") as string}>
                          <IconButton
                            onClick={() => deleteAction(row)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                )}
                {Object.values(row).map((value: any, j: number) => (
                  <Box component="td" key={j}>
                    {value}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <TablePagination
        component="div"
        count={totalRows}
        page={page - 1} // Adjust for 0-based index
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]} // Hide rows per page change if not needed
        labelRowsPerPage="Sorok száma:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />
    </Paper>
  );
}
